import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIconButton from './DeleteIconButton';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import LockIcon from '@mui/icons-material/LockOutlined';
// import FileCopyIcon from '@mui/icons-material/FileCopy';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, Grid, IconButton, InputLabel, List, ListItem, ListItemButton, ListItemIcon, ListItemText, MenuItem, Select, Switch, TextField, Tooltip, Typography, useTheme } from '@mui/material';
// import { makeStyles } from '@mui/styles';
// import { DatePicker } from '@mui/x-date-pickers/DatePicker';
// import { Editor } from '@tinymce/tinymce-react';
// import _ from 'lodash';
// import moment from 'moment';
import { useSnackbar } from 'notistack';
// import QRCode from 'qrcode.react';
import React from 'react';
// import { useDropzone } from 'react-dropzone';

// import { DataAPIContext, GET_LIST } from '../api';
// import AccessTagsDropdown from '../components/AccessTagsDropdown';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FormActionButtons from '../components/FormActionButtons';
import RemoteAutocomplete from '../components/RemoteAutocomplete';
// import RruleSetScheduler from '../components/RruleSet';
// import TimezonePicker from '../components/TimezonePicker';
// import { TripDayUploadLabel } from '../components/TripflowEditor';
// import UploadImageButton from '../components/UploadImageButton';
// import config from '../config';
// import { ContextStore } from '../store/contextStore';
import { copyTextToClipboard } from '../utils/clipboard';
// import validURL from '../utils/validators/url';
import { Stack } from '@mui/system';

// adopted from https://sashamaps.net/docs/resources/20-colors/
const colorList = [
    '#e6194b', '#3cb44b', '#ffe119', '#4363d8', '#f58231', '#911eb4', '#46f0f0', '#f032e6', '#bcf60c', '#fabebe', '#008080', '#e6beff', '#9a6324', '#fffac8', '#800000', '#aaffc3', '#808000', '#ffd8b1', '#000075', '#808080', '#ffffff', '#000000'
];

export const ID_TYPE_OPTIONS = [
    { value: 'product_id', label: 'Product ID' },
    { value: 'option_id', label: 'Option ID (Ventrata)' },
    { value: 'product_with_departure_time', label: 'Product ID + departure time (ID/hh:mm)' },
    { value: 'product_with_departure_weekday', label: 'Product ID + departure weekday (ID/weekday)' },
    { value: 'product_with_departure_weekday_and_time', label: 'Product ID + weekday + time (ID/weekday/hh:mm)' },
    { value: 'rate_id', label: 'Product ID + rate (Bokun, ID/RATE_ID)' },
    { value: 'other', label: 'Other / templated' },
];

const ID_TYPE_LABEL_BY_VALUE = ID_TYPE_OPTIONS.reduce((acc, opt) => {
    acc[opt.value] = opt.label;
    return acc;
}, {});

export const DEFAULT_AEID_TYPE = 'product_id';

/**
 * One of a tour's additional external IDs (backend `Additional_Ext_ID`, serialized by
 * `Additional_Ext_IDInlineSerializer` as an item of the tour's `additional_custom_ids`).
 *
 * @typedef {Object} AdditionalExternalID
 * @property {number} [id] - absent on a row added in this editor and not saved yet
 * @property {string} custom_id
 * @property {string} [id_type]
 * @property {boolean} [use_for_matching]
 * @property {boolean} [api_queryable]
 * @property {?string} [integration]
 * @property {?string} [restech_account]
 * @property {boolean} [managed] - true: owned by an import (product sync / XML), which updates
 *     it on every sync; false: owned by the operator, never touched by an import. On save this
 *     is the requested state: false takes a managed row over, true hands it back.
 * @property {Array<Object>} [tour_user_tags]
 * @property {Array<{id: number, name: string}>} [targets]
 * @property {number} [idx] - UI only: position in the list (`trips/utils.js` reformatCustomIds)
 * @property {boolean} [_stored_managed] - UI only: `managed` as the server held it when the tour
 *     was loaded or last saved (reformatCustomIds); stripped from the save payload
 * @property {Object} [_stored_row] - UI only, managed rows: the row as the server holds it, which
 *     undoing a take-over restores (reformatCustomIds); stripped from the save payload
 */

/*
 * Managed (import-owned) external IDs -- backend plans/2026-10-06-managed-ext-ids.md.
 *
 * On a save the server applies only `managed` to a row it holds as managed -- unless the same
 * save takes it over (managed=false), which applies the whole row -- and never deletes a row it
 * holds as managed for being absent from the list. So a row is read-only while it is (or is
 * being handed back as) managed, and a take-over is editable at once but removable only once
 * the tour has been saved.
 */
export const isAeidReadOnly = (aeid) => !!aeid?.managed;

export const canRemoveAeid = (aeid) => !aeid?.managed && !aeid?._stored_managed;

/** 'managed' | 'takeover_pending' | 'handback_pending' | null (an operator-owned row). */
export const managedState = (aeid) => {
    if (aeid?._stored_managed) {
        return aeid.managed ? 'managed' : 'takeover_pending';
    }
    return aeid?.managed ? 'handback_pending' : null;
};

/**
 * "Hand back to import" is offered on any saved operator-owned row; without an integration the
 * owner is the direct XML import, which owns every managed row of the tour. A row taken over in
 * this session is handed back by undoing the take-over (managedState 'takeover_pending'); a new
 * row cannot be handed back at all -- the server creates every editor-made row as operator-owned.
 */
export const canHandBack = (aeid) => !!aeid && managedState(aeid) === null && aeid.id != null;

export const managedOwnerLabel = (aeid) => {
    if (!aeid?.integration) {
        return 'XML import';
    }
    return aeid.restech_account ? `${aeid.integration} / ${aeid.restech_account}` : aeid.integration;
};

// Per managedState(): the list chip, the edit dialog's notice (its text is also the chip's
// tooltip) and the ownership change that notice offers -- `managed` is the value it requests.
const MANAGED_STATES = {
    managed: {
        chip: 'Managed', color: 'primary', severity: 'info',
        text: (owner) => `Maintained by an import (${owner}). Take it over to edit.`,
        action: 'Take over', managed: false,
    },
    handback_pending: {
        chip: 'Handed back (unsaved)', color: 'primary', severity: 'info',
        text: (owner) => `Handed back to the import (${owner}). Takes effect when you save the tour.`,
        action: 'Undo', managed: false,
    },
    takeover_pending: {
        chip: 'Taken over (unsaved)', color: 'default', severity: 'warning',
        text: (owner) => `Taken over from the import (${owner}): it stops updating this ID when you save the tour, and you can remove the ID after that. Undo restores the import's version.`,
        action: 'Undo', managed: true,
    },
};

const handBackWarning = (aeid) => (aeid.integration
    ? `The next sync (${managedOwnerLabel(aeid)}) will overwrite manual edits to this ID, and delete it if the feed no longer lists it.`
    : 'The next XML import will maintain this ID: it overwrites manual edits and deletes the ID if the import no longer lists it.');

const ManagedChip = ({ aeid }) => {
    const ms = MANAGED_STATES[managedState(aeid)];
    if (!ms) {
        return null;
    }
    return (
        <Tooltip title={ms.text(managedOwnerLabel(aeid))}>
            <Chip label={ms.chip} size="small" variant="outlined" color={ms.color} />
        </Tooltip>
    );
};

const ANY_INTEGRATION_OPTION = { id: null, name: '(Any / unspecified)' };
const NO_ACCOUNT_OPTION = { id: null, name: '(No account)' };

const isOptionEqualToValueWithNull = (option, value) => {
    const a = option == null ? null : option.id ?? null;
    const b = value == null ? null : value.id ?? null;
    return a === b;
};

function normalizeTourTagSearchQuery(input, tagCategories) {
    if (input == null || input === '') return input;
    if (!tagCategories?.length) return input;
    const trimmed = input.trim();
    const m = trimmed.match(/^([^:]+):\s*(.*)$/);
    if (!m) return input;
    const maybeCode = m[1].trim().toLowerCase();
    const rest = m[2].trim();
    const known = tagCategories.some(
        (c) => c?.category?.code && c.category.code.toLowerCase() === maybeCode
    );
    if (!known) return input;
    return rest;
}

function rankTourTagOption(option, inputRaw, getOptionLabel) {
    const input = (inputRaw || '').trim();
    if (!input) return 0;
    const label = getOptionLabel(option);
    const li = label.toLowerCase();
    const qi = input.toLowerCase();
    if (li === qi) return 100;
    if (li.startsWith(qi)) return 92;
    const m = input.match(/^([^:]+):\s*(.*)$/);
    const oc = (option.category?.code || '').toLowerCase();
    const on = (option.name || '').toLowerCase();
    if (m) {
        const code = m[1].trim().toLowerCase();
        const rest = m[2].trim().toLowerCase();
        if (oc === code) {
            if (rest) {
                if (on === rest) return 98;
                if (on.startsWith(rest)) return 88;
                if (on.includes(rest)) return 68;
            } else {
                return 58;
            }
        } else if (rest && on.includes(rest)) {
            return 22;
        }
    }
    if (li.includes(qi)) return 50;
    if (on.includes(qi)) return 40;
    return 10;
}

function sortTourTagAutocompleteOptions(options, inputValue, getOptionLabel) {
    if (!options?.length) return options;
    const q = (inputValue || '').trim();
    if (!q) return options;
    const scored = options.map((opt) => ({
        opt,
        rank: rankTourTagOption(opt, q, getOptionLabel),
    }));
    scored.sort((a, b) => {
        if (b.rank !== a.rank) return b.rank - a.rank;
        return getOptionLabel(a.opt).localeCompare(getOptionLabel(b.opt), undefined, { sensitivity: 'base' });
    });
    return scored.map((s) => s.opt);
}

function buildSecondaryText(aeid, catColor, theme) {
    const chunks = [];
    if (aeid.tour_user_tags && aeid.tour_user_tags.length > 0) {
        const bycat = {};
        const tagStack = [];
        aeid.tour_user_tags.forEach((tag) => {
            if (!bycat[tag.category.code]) {
                bycat[tag.category.code] = {
                    name: tag.category.name,
                    color: catColor[tag.category.code],
                    tags: [],
                };
            }
            bycat[tag.category.code].tags.push(tag.name);
        });
        Object.keys(bycat).forEach((catid) => {
            const cat = bycat[catid];
            cat.tags.forEach((tag, tagidx) => {
                tagStack.push(
                    <Chip
                        key={`${catid}-${tag}-${tagidx}`}
                        style={{
                            backgroundColor: cat.color,
                            color: theme.palette.getContrastText(cat.color),
                        }}
                        variant="filled"
                        label={`${cat.name}: ${tag}`}
                        size="small"
                    />
                );
            });
        });
        chunks.push(
            <Stack key="tags" spacing={1} direction="row" useFlexGap flexWrap="wrap">
                {tagStack}
            </Stack>
        );
    }
    if (aeid.targets && aeid.targets.length > 0) {
        const tourStack = [];
        aeid.targets.forEach((target, idx) => {
            tourStack.push(
                <Chip key={target.id || idx} label={`Tour: ${target.name}`} variant="outlined" size="small" />
            );
        });
        chunks.push(
            <Stack key="tours" spacing={1} direction="row" useFlexGap flexWrap="wrap">
                {tourStack}
            </Stack>
        );
    }
    return <Stack direction="column" spacing={1}>{chunks}</Stack>;
}

const ExternalIDListItem = React.memo(function ExternalIDListItem({ aeid, idx, catColor, onEditShow, onDelete, copyToClipboard, theme, hasWritePerm }) {
    const secondaryText = buildSecondaryText(aeid, catColor, theme);
    const matchingOff = aeid.use_for_matching === false;
    const apiOff = aeid.api_queryable === false;
    const typeLabel = ID_TYPE_LABEL_BY_VALUE[aeid.id_type] || ID_TYPE_LABEL_BY_VALUE[DEFAULT_AEID_TYPE];
    const integrationChipLabel = aeid.integration
        ? (aeid.restech_account ? `${aeid.integration} / ${aeid.restech_account}` : aeid.integration)
        : null;
    // A row the server holds as managed is never deleted for being absent from the saved list --
    // removing it here would be undone by the save. Take it over and save first.
    return (
        <ListItem
            disableGutters
            divider
            secondaryAction={!canRemoveAeid(aeid) ? undefined : (
                <DeleteIconButton edge="end" aria-label="delete" allowed={hasWritePerm} onClick={() => onDelete(aeid, idx)} />
            )}
        >
            <ListItemButton onClick={(ev) => onEditShow(ev, idx)} dense disableGutters disableRipple>
                <ListItemIcon>
                    {isAeidReadOnly(aeid) ? <LockIcon fontSize="small" /> : <EditIcon fontSize="small" />}
                </ListItemIcon>
                <ListItemText
                    primary={
                        <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                            <Typography
                                variant="h6"
                                sx={matchingOff ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}
                            >
                                {aeid.custom_id}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={(ev) => {
                                    copyToClipboard(aeid.custom_id, `External ID`);
                                    ev.stopPropagation();
                                    ev.preventDefault();
                                }}
                            >
                                <FileCopyOutlinedIcon fontSize="small" />
                            </IconButton>
                            <Chip label={typeLabel} size="small" variant="outlined" />
                            <ManagedChip aeid={aeid} />
                            {integrationChipLabel && (
                                <Chip label={integrationChipLabel} size="small" variant="outlined" color="info" />
                            )}
                            {matchingOff && (
                                <Chip label="matching off" size="small" color="warning" variant="outlined" />
                            )}
                            {apiOff && (
                                <Chip label="API off" size="small" color="warning" variant="outlined" />
                            )}
                        </Stack>
                    }
                    secondary={secondaryText}
                    secondaryTypographyProps={{ component: 'div' }}
                />
            </ListItemButton>
        </ListItem>
    );
});

const AdditionalExternalIDEditDialog = (props) => {
    const { enqueueSnackbar } = useSnackbar();
    const [aeid, setAeid] = React.useState(props.data);
    // Which ownership change awaits confirmation: false = take over, true = hand back.
    const [confirmManaged, setConfirmManaged] = React.useState(null);

    const ms = MANAGED_STATES[managedState(aeid)];
    const managed = isAeidReadOnly(aeid);
    const readOnly = !props.hasWritePerm || managed;
    const owner = managedOwnerLabel(aeid);

    const onSave = () => {
        const trimmedId = (aeid.custom_id ?? '').trim();
        if (!trimmedId) {
            enqueueSnackbar('External ID must not be empty', { variant: 'error' });
            return;
        }
        props.onChange && props.onChange({ ...aeid, custom_id: trimmedId }, props.idx);
        props.onClose();
    };

    // An ownership change is applied to the tour at once (it was confirmed) and the dialog stays
    // open on the result -- a take-over is editable right away. It carries no unapplied field
    // edits: the row is taken as last applied, or, undoing a take-over, as the server holds it.
    const changeOwnership = (nextManaged) => {
        const row = nextManaged && props.data._stored_row ? { ...props.data, ...props.data._stored_row } : props.data;
        const next = { ...row, managed: nextManaged };
        props.onChange && props.onChange(next, props.idx);
        setAeid(next);
        setConfirmManaged(null);
    };

    // Undoing a change that is not saved yet restores what the server holds -- nothing to confirm.
    const requestManaged = (nextManaged) => {
        if (nextManaged === !!aeid._stored_managed) {
            changeOwnership(nextManaged);
        } else {
            setConfirmManaged(nextManaged);
        }
    };

    const onFieldValueChange = (ev) => {
        setAeid((prev) => ({...prev, [ev.target.name]: ev.target.value}));
    }

    const onSwitchChange = (ev) => {
        setAeid((prev) => ({...prev, [ev.target.name]: ev.target.checked}));
    }

    const onIntegrationChange = (ev, nv) => {
        const nextIntegration = nv?.id || null;
        setAeid((prev) => ({
            ...prev,
            integration: nextIntegration,
            restech_account: prev.integration === nextIntegration ? prev.restech_account : null,
        }));
    }

    const onAccountChange = (ev, nv) => {
        setAeid((prev) => ({...prev, restech_account: nv?.id || null}));
    }

    const onTagsChange = (ev, nv) => {
        setAeid((prev) => ({...prev, tour_user_tags: nv}));
    }

    const onToursChange = (ev, nv) => {
        setAeid((prev) => ({...prev, targets: nv}));
    }

    const tagGetOptionLabel = React.useCallback(
        (option) => option.category?.code + ': ' + option.name,
        []
    );

    return (<Dialog onClose={props.onClose} open={!!props.open} maxWidth="md" fullWidth>
        <DialogTitle>{managed ? 'External ID' : 'Edit External ID'}: {aeid.custom_id}</DialogTitle>
        <IconButton aria-label="close" onClick={props.onClose} sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500], }}>
          <CloseIcon />
        </IconButton>
        <ConfirmationDialog
            open={confirmManaged !== null}
            title={confirmManaged ? 'Hand back to import?' : 'Take over this external ID?'}
            okButtonText={confirmManaged ? 'Hand back' : 'Take over'}
            onCancel={() => setConfirmManaged(null)}
            onConfirm={() => changeOwnership(confirmManaged)}
        >
            {confirmManaged
                ? handBackWarning(aeid)
                : `The import (${owner}) will stop updating this ID and will no longer remove it. You can edit it right away; removing it has to wait until you save the tour.`}
        </ConfirmationDialog>
        <DialogContent>
            {ms && (
                <Box mb={2}>
                    <Alert
                        severity={ms.severity}
                        action={props.hasWritePerm ? (
                            <Button color="inherit" size="small" onClick={() => requestManaged(ms.managed)}>{ms.action}</Button>
                        ) : undefined}
                    >
                        {ms.text(owner)}
                    </Alert>
                </Box>
            )}
            <Grid container direction="row" spacing={2}>
                <Grid item xs={12}>
                    <TextField
                        variant="filled"
                        name="custom_id"
                        label="External ID"
                        value={aeid.custom_id}
                        onChange={onFieldValueChange}
                        fullWidth
                        disabled={readOnly}
                        size="small"
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormControl variant="filled" size="small" fullWidth disabled={readOnly}>
                        <InputLabel id={`aeid-id-type-label-${props.idx}`}>ID Type</InputLabel>
                        <Select
                            labelId={`aeid-id-type-label-${props.idx}`}
                            name="id_type"
                            value={aeid.id_type || DEFAULT_AEID_TYPE}
                            onChange={onFieldValueChange}
                            label="ID Type"
                        >
                            {ID_TYPE_OPTIONS.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControlLabel
                        control={
                            <Switch
                                name="use_for_matching"
                                checked={aeid.use_for_matching !== false}
                                onChange={onSwitchChange}
                                disabled={readOnly}
                            />
                        }
                        label="Use for product → tour matching"
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormControlLabel
                        control={
                            <Switch
                                name="api_queryable"
                                checked={aeid.api_queryable !== false}
                                onChange={onSwitchChange}
                                disabled={readOnly}
                            />
                        }
                        label="Can be used to query the integration API"
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <RemoteAutocomplete
                        disabled={readOnly}
                        resource="integration"
                        urlSuffix={null}
                        label="Booking system"
                        variant="filled"
                        size="small"
                        value={aeid.integration ? { id: aeid.integration, name: aeid.integration } : ANY_INTEGRATION_OPTION}
                        onChange={onIntegrationChange}
                        mapResults={(data) => [ANY_INTEGRATION_OPTION, ...data]}
                        isOptionEqualToValue={isOptionEqualToValueWithNull}
                        filterOptions={(options) => options}
                        disableClearable
                        resetQueryOnOpen
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <RemoteAutocomplete
                        key={`account-${aeid.integration || 'none'}`}
                        disabled={readOnly || !aeid.integration}
                        resource={aeid.integration ? `integration/${aeid.integration}/accounts` : 'integration'}
                        urlSuffix={null}
                        label="Account alias"
                        variant="filled"
                        size="small"
                        value={aeid.restech_account ? { id: aeid.restech_account, name: aeid.restech_account } : NO_ACCOUNT_OPTION}
                        onChange={onAccountChange}
                        mapResults={(data) => [NO_ACCOUNT_OPTION, ...(aeid.integration ? data : [])]}
                        isOptionEqualToValue={isOptionEqualToValueWithNull}
                        filterOptions={(options) => options}
                        disableClearable
                        resetQueryOnOpen
                        textFieldProps={{
                            helperText: aeid.integration
                                ? 'Optional — leave as "(No account)" if this ID is not account-scoped'
                                : 'Pick a booking system first',
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <RemoteAutocomplete
                        disabled={readOnly}
                        multiple
                        normalizeQuery={(q) => normalizeTourTagSearchQuery(q, props.tagCategories)}
                        apiFilters={[{
                            field: 'category', op: 'eq', value: [...props.tagCategories?.map((cat) => cat?.category?.id?.toString())]
                        }]}
                        getOptionLabel={tagGetOptionLabel}
                        resource="tour_tag"
                        urlSuffix={null}
                        label="Tags"
                        variant="filled"
                        size="small"
                        onChange={onTagsChange}
                        value={aeid?.tour_user_tags}
                        renderTags={(value, getTagProps) => value.map((tag, index) => (
                            <Chip
                                key={tag.id}
                                {...getTagProps({ index })}
                                label={
                                    <a
                                        href={`/tags/${tag.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={(e) => e.stopPropagation()}
                                        style={{ textDecoration: 'none', color: 'inherit' }}
                                    >
                                        {`${tag.category?.code}: ${tag.name}`}
                                    </a>
                                }
                            />
                        ))}
                        filterOptions={(options, state) => {
                            const filtered = options.filter(
                                (option) => !aeid?.tour_user_tags?.some((selected) => selected.id === option.id)
                            );
                            return sortTourTagAutocompleteOptions(
                                filtered,
                                state?.inputValue,
                                tagGetOptionLabel
                            );
                        }}
                        textFieldProps={{
                            helperText: `When a participant is added to this tour using this external ID, they will be assigned these tags`
                        }}
                    />
                </Grid>
                <Grid item xs={12}>
                    <RemoteAutocomplete
                        disabled={readOnly}
                        multiple
                        apiFilters={[{ field: 'time', value: 'nonbespoke' }]}
                        getOptionLabel={(opt) => {
                            if (!opt.is_bespoke) {
                                return '[id:' + opt.id + '] ' + opt.name + ' (Recurring)';
                            }
                            return '[id:' + opt.id + '] ' + opt.name + ' (' + opt.start_date + ' - ' + opt.end_date + ')';
                        }}
                        renderOption={(props, option) => {
                            if (option.is_bespoke) {
                                return (
                                    <div {...props}>
                                        [id:{option.id}] {option.name} ({option.start_date} - {option.end_date})
                                    </div>
                                );
                            }
                            return (
                                <div {...props}>
                                    [id:{option.id}] {option.name} (Recurring)
                                </div>
                            );
                        }}
                        resource="tour"
                        label="Related tours"
                        variant="filled"
                        size="small"
                        onChange={onToursChange}
                        value={aeid?.targets}
                        filterOptions={(options) => 
                            options.filter(option => 
                                !aeid?.targets?.some(selected => selected.id === option.id)
                            )
                        }
                        textFieldProps={{
                            helperText: `When a participant is added to this tour using this external ID, they will additionally become participants of these tours (same date & time)`
                        }}
                    />
                </Grid>
            </Grid>
            <DialogActions>
                {props.hasWritePerm && canHandBack(aeid) && (
                    <Button onClick={() => requestManaged(true)} sx={{ mr: 'auto' }}>Hand back to import</Button>
                )}
                {managed ? (
                    <Button onClick={props.onClose}>Close</Button>
                ) : (
                    /* Not "Save": this only updates the trip form; the trip's own SAVE persists it. */
                    <FormActionButtons
                        mode="edit"
                        submitLabel="Apply & close"
                        submitIcon={null}
                        onSubmit={onSave}
                        submitDisabled={!(aeid.custom_id ?? '').trim()}
                        onCancel={props.onClose}
                        size="medium"
                    />
                )}
            </DialogActions>
        </DialogContent>
    </Dialog>);
}

export const AdditionalExternalIDManager = (props) => {
    const theme = useTheme();
    const { enqueueSnackbar } = useSnackbar();
    const [aeidIdx, setAeidIdx] = React.useState(null);

    const showDialog = typeof +aeidIdx === 'number' && props.data[aeidIdx];

    const onDeleteRef = React.useRef(props.onDelete);
    onDeleteRef.current = props.onDelete;

    const copyToClipboard = React.useCallback((text, label) => {
        if (text) {
            copyTextToClipboard(text);
            enqueueSnackbar(label ? `${label} copied!` : 'Copied!', { variant: 'success', autoHideDuration: 750 });
        } else {
            enqueueSnackbar(label ? `${label} is empty!` : 'Cannot copy empty string!', { variant: 'error', autoHideDuration: 750 });
        }
    }, [enqueueSnackbar]);

    const onEditShow = React.useCallback((ev, idx) => {
        setAeidIdx(idx);
    }, []);

    const onEditClose = React.useCallback(() => {
        setAeidIdx(null);
    }, []);

    const handleListDelete = React.useCallback((aeid, idx) => {
        onDeleteRef.current(aeid, idx);
    }, []);

    const catColor = React.useMemo(() => {
        const cc = {};
        (props.data || []).forEach((aeid) => {
            if (aeid.tour_user_tags && aeid.tour_user_tags.length > 0) {
                aeid.tour_user_tags.forEach((tag) => {
                    if (!cc[tag.category.code]) {
                        cc[tag.category.code] = colorList[Object.keys(cc).length % colorList.length];
                    }
                });
            }
        });
        return cc;
    }, [props.data]);

    return (<>
        {showDialog && (<AdditionalExternalIDEditDialog
            key={aeidIdx}
            open={showDialog}
            onClose={onEditClose}
            onChange={props.onChange}
            data={props.data[aeidIdx]}
            idx={aeidIdx}
            hasWritePerm={props.hasWritePerm}
            tagCategories={props.tagCategories}
        />)}
        <Stack direction="column">
            { (props.data || []).length > 0 && (
                <List dense>
            { (props.data || []).map((aeid, idx) => (
                        <ExternalIDListItem
                            key={aeid.custom_id || idx}
                            aeid={aeid}
                            idx={idx}
                            catColor={catColor}
                            onEditShow={onEditShow}
                            onDelete={handleListDelete}
                            copyToClipboard={copyToClipboard}
                            theme={theme}
                            hasWritePerm={props.hasWritePerm}
                        />
                    )) }
                </List>
            ) }
            <Box>
                <Button variant="outlined" color="primary" onClick={props.onAdd}>
                    <AddIcon fontSize="small"/>&nbsp;Add auxiliary external ID
                </Button>
            </Box>
        </Stack>
    </>)
}

export default AdditionalExternalIDManager;
